/**
 * subconAccess — who may open SUBCONTRACTORS.
 *
 * Mikko, 2026-09-15: "the Only Access should be me, Paolo Obrero, Evelyn
 * Canaria, Lailani Zubiaga, Maricris Lavapiez … the other user can see that
 * module subcontractor but they should not open that one module."
 *
 * The module prices and pays ninety-three subcontractors: bank accounts, TINs,
 * what each person earned last week. So it is default-DENY, the same way HRIS
 * is (hrisAccess.ts), with one deliberate difference HE ASKED FOR: the HOME
 * TILE STAYS VISIBLE. HRIS hides its tile from accounts without access; this
 * one shows as present-but-locked, so the floor can see the module exists and
 * knows who to ask, rather than wondering whether it was taken away.
 *
 * ── THIS GATES THE APP UI ONLY ────────────────────────────────────────────
 * It is not a substitute for SharePoint permissions. Anyone whose account can
 * read `iSyn2_SubconPayouts`, `iSyn2_SubconPayrollProfiles` and the rest can
 * still reach that data through the SharePoint API or the list views, with or
 * without this app. To actually prevent leakage, restrict those lists in
 * SharePoint too. This stops a screen opening; it does not stop a read.
 *
 * ── MATCHED ON THE SIGN-IN ADDRESS, AND ONLY THAT ─────────────────────────
 * Not the display name. A directory name is whatever the directory says it is
 * and two people can share one; the address is unforgeable from inside the
 * app. Mikko sent the four M365 accounts on 2026-09-15, so the list below is
 * the whole rule — five accounts, no fallbacks, no partial matching.
 *
 * Overridable via env without a code change, comma-separated:
 *   VITE_SUBCON_ACCESS_USERS
 */
import { useMemo } from "react";
import { useMsal } from "@azure/msal-react";

const lc = (s?: string | null): string => (s ?? "").trim().toLowerCase();

/**
 * The accounts that may open the module.
 *
 * `operations@` also appears in HRIS_LIMITED_ACCESS with a narrower grant.
 * That is not a conflict: the two modules gate independently, and an account
 * limited to Leaves in HRIS may still be the person who runs subcon payroll.
 */
export const SUBCON_ACCESS_USERS: readonly string[] = (() => {
  const env = import.meta.env?.VITE_SUBCON_ACCESS_USERS as string | undefined;
  if (env) return env.split(",").map(lc).filter(Boolean);
  return [
    // Mikko — both the tenant account and the address he signs in with.
    "mikko@intalgarmentsopc.onmicrosoft.com",
    "intalmiks11@gmail.com",
    "hrandpurchasing@intalgarmentsopc.onmicrosoft.com",
    "operations@intalgarmentsopc.onmicrosoft.com",
    "finance1@intalgarmentsopc.onmicrosoft.com",
    "finance3@intalgarmentsopc.onmicrosoft.com",
    // The owner, Marvin Intal (the CEO) — added 2026-10-13 on Mikko's say-so.
    "managingowner@intalgarmentsopc.onmicrosoft.com",
  ];
})();

/**
 * May this account open SUBCONTRACTORS?
 *
 * Pure, so the rule can be tested without a signed-in browser. An account with
 * no address — which is what a half-initialised MSAL cache looks like — is
 * refused: default-deny means the absence of evidence is not evidence of
 * access.
 */
export function canOpenSubcon(username?: string | null): boolean {
  const email = lc(username);
  return !!email && SUBCON_ACCESS_USERS.includes(email);
}

/** The same answer, for a component. */
export function useSubconAccess(): boolean {
  const { accounts } = useMsal();
  const username = accounts[0]?.username;
  return useMemo(() => canOpenSubcon(username), [username]);
}
